import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { DownloadIcon, ListChecksIcon, MoreVerticalIcon, SearchIcon, Settings2Icon, TagIcon, UsersIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { bulkUpdateStage, deleteLead, updateLeadStage } from '../../services/leadService';
import { usePreferences } from '../../hooks/usePreferences';
import { DEFAULT_LEAD_COLUMNS, DEFAULT_LEADS_PER_PAGE, LEADS_PER_PAGE_OPTIONS } from '../../services/preferencesService';
import { FloatingPanel } from '../../components/shared/FloatingPanel';
import { downloadCsv } from '../../utils/csv';
import { formatDate, formatDateTime, fullName } from '../../utils/format';
import { Lead, LeadNote } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { Table } from '../../components/ui/Table';
import { FloatingMenu } from '../../components/shared/FloatingMenu';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageBadge } from '../../components/crm/StageBadge';
import { StageManager } from '../../components/crm/StageManager';
import { SourceManager } from '../../components/crm/SourceManager';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { noteColor } from '../../components/crm/noteColors';
import { LeadDrawer } from '../../components/crm/LeadDrawer';

/**
 * Paginación en cliente mientras los datos son de demostración.
 *
 * TODO(Supabase): sustituir por una consulta paginada en servidor
 * (`.range(offset, offset + limit - 1)`) que reciba los mismos filtros,
 * búsqueda y orden, para no descargar todos los leads al navegador.
 * `pageSize` y `currentPage` ya son los únicos valores necesarios.
 */

/**
 * Columnas disponibles en la tabla de leads.
 * TODO(Supabase): guardar la selección por usuario en `user_preferences`.
 */
const COLUMN_OPTIONS: {
  key: string;
  label: string;
}[] = [{
  key: 'name',
  label: 'Nombre'
}, {
  key: 'company',
  label: 'Empresa'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'phone',
  label: 'Teléfono'
}, {
  key: 'instagram',
  label: 'Instagram'
}, {
  key: 'city',
  label: 'Ciudad'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'adBudget',
  label: 'Inversión actual'
}, {
  key: 'budget',
  label: 'Presupuesto disponible'
}, {
  key: 'source',
  label: 'Fuente'
}, {
  key: 'campaign',
  label: 'Campaña'
}, {
  key: 'landing',
  label: 'Landing'
}, {
  key: 'form',
  label: 'Formulario'
}, {
  key: 'stage',
  label: 'Estado'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'createdAt',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}, {
  key: 'note',
  label: 'Última nota'
}];
/** Columnas activas la primera vez, en este orden. */
const DEFAULT_COLUMNS = DEFAULT_LEAD_COLUMNS;
export function Leads() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    forms,
    landings,
    notes,
    isDemo
  } = useWorkspace();
  const members = useWorkspaceMembers();
  const [drawerLead, setDrawerLead] = useState<string | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [confirmTrash, setConfirmTrash] = useState<Lead | null>(null);
  const [trashing, setTrashing] = useState(false);
  const [preferences, updatePreferences] = usePreferences();
  const visibleColumns = preferences.leadColumns;
  const setVisibleColumns = (updater: string[] | ((prev: string[]) => string[])) => updatePreferences({
    leadColumns: typeof updater === 'function' ? updater(visibleColumns) : updater
  });
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const [search, setSearch] = useState('');
  const [stageId, setStageId] = useState('');
  const [formId, setFormId] = useState('');
  const [landingId, setLandingId] = useState('');
  const [campaign, setCampaign] = useState('');
  const [owner, setOwner] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkStage, setBulkStage] = useState('');
  const [applying, setApplying] = useState(false);
  const campaigns = useMemo(() => [...new Set(leads.map(l => l.origin.campaign).filter(Boolean))], [leads]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter(lead => {
      if (term && ![lead.firstName, lead.lastName, lead.company, lead.email, lead.phone].join(' ').toLowerCase().includes(term)) return false;
      if (stageId && lead.stageId !== stageId) return false;
      if (formId && lead.origin.formId !== formId) return false;
      if (landingId && lead.origin.landingId !== landingId) return false;
      if (campaign && lead.origin.campaign !== campaign) return false;
      if (owner && lead.assignedTo !== owner) return false;
      if (from && lead.createdAt < from) return false;
      if (to && lead.createdAt > `${to}T23:59:59`) return false;
      return true;
    });
  }, [leads, search, stageId, formId, landingId, campaign, owner, from, to]);
  const pageSize = preferences.leadsPerPage || DEFAULT_LEADS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * pageSize;
  const paged = filtered.slice(offset, offset + pageSize);
  const rangeFrom = filtered.length === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + pageSize, filtered.length);

  /** Números de página a mostrar, con la actual siempre centrada. */
  const pageNumbers = useMemo(() => {
    const max = 5;
    if (totalPages <= max) return Array.from({
      length: totalPages
    }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - max + 1));
    return Array.from({
      length: max
    }, (_, i) => start + i);
  }, [currentPage, totalPages]);
  function stageName(lead: Lead) {
    return stages.find(s => s.id === lead.stageId)?.name ?? 'Nuevo';
  }
  function memberName(id?: string) {
    const member = members.find(m => m.userId === id);
    return member ? fullName(member.firstName, member.lastName) : 'Sin asignar';
  }

  /** Última nota del lead, para la vista previa en la tabla. */
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  function exportCsv() {
    downloadCsv('leads-ava.csv', ['Nombre', 'Apellidos', 'Empresa', 'Email', 'Teléfono', 'Instagram', 'Ciudad', 'País', 'Servicio solicitado', 'Presupuesto publicidad', 'Presupuesto disponible', 'Formulario', 'Landing', 'Fuente', 'Campaña', 'Estado', 'Comercial', 'Fecha', 'Última nota'], filtered.map(lead => [lead.firstName, lead.lastName, lead.company, lead.email, lead.phone, lead.instagram ?? '', lead.city ?? '', lead.country ?? '', lead.service ?? '', lead.adBudget ?? '', lead.budget ?? '', forms.find(f => f.id === lead.origin.formId)?.name ?? '', landings.find(l => l.id === lead.origin.landingId)?.name ?? '', lead.origin.source, lead.origin.campaign, stageName(lead), memberName(lead.assignedTo), formatDate(lead.createdAt), lastNote(lead.id)?.body ?? '']));
  }

  /** Menú de acciones rápidas de cada fila o tarjeta. */
  function rowActions(lead: Lead) {
    const items = [{
      label: 'Abrir lead',
      onSelect: () => setDrawerLead(lead.id)
    }, {
      label: 'Abrir WhatsApp',
      onSelect: () => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank', 'noopener')
    }, {
      label: 'Copiar teléfono',
      onSelect: () => navigator.clipboard?.writeText(lead.phone)
    }];
    if (hasPermission('leads.edit')) {
      items.splice(1, 0, {
        label: 'Añadir nota',
        onSelect: () => setDrawerLead(lead.id)
      });
    }
    if (hasPermission('leads.delete')) {
      items.push({
        label: 'Mover a papelera',
        destructive: true,
        onSelect: () => setConfirmTrash(lead)
      } as never);
    }
    return items;
  }
  async function applyBulk() {
    if (!bulkStage || selected.length === 0) return;
    setApplying(true);
    await bulkUpdateStage(selected, bulkStage, user ? fullName(user.firstName, user.lastName) : 'Sistema');
    setApplying(false);
    setSelected([]);
    setBulkStage('');
  }
  if (leads.length === 0) {
    return <>
        <PageHeader title="Leads" description="Contactos recibidos por tu empresa." data-id="element-1382" data-mp="394c57o9dsth0" />
        <EmptyState icon={<UsersIcon className="h-5 w-5" data-id="element-1384" data-mp="349y6pczfa684" />} title="Todavía no tienes contactos" description="Publica una landing o comparte un formulario para comenzar a recibir leads." action={<Link to="/app/landings" data-id="element-1385" data-mp="2vcnplazxbmva">
              <Button data-id="element-1386" data-mp="3s1ddpj259bpp">Ir a landings</Button>
            </Link>} data-id="element-1383" data-mp="349octeb9bex5" />
      </>;
  }
  return <>
      <PageHeader title="Leads" description={`${filtered.length} contactos que cumplen los filtros.`} actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1388" data-mp="3j05mfky1pb54" />}
            <Button variant="secondary" leftIcon={<DownloadIcon className="h-4 w-4" data-id="element-1390" data-mp="3e1uczbbnx2nf" />} onClick={exportCsv} data-id="element-1389" data-mp="3iyucf3e0b75k">
              Exportar CSV
            </Button>
            <Button variant="secondary" leftIcon={<Settings2Icon className="h-4 w-4" data-id="element-1392" data-mp="27mtila2acsiy" />} onClick={() => setColumnsOpen(true)} data-id="element-1391" data-mp="27ls2usabrhuw">
              Personalizar columnas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1394" data-mp="9f26dl9pepo9" />} onClick={() => setStagesOpen(true)} data-id="element-1393" data-mp="9dr5bt7n7w0u">
              Gestionar estados
            </Button>
            <Button variant="secondary" leftIcon={<TagIcon className="h-4 w-4" data-id="element-1396" data-mp="i7mc15oexuil" />} onClick={() => setSourcesOpen(true)} data-id="element-1395" data-mp="i6kmxxakb7hv">
              Gestionar fuentes
            </Button>
          </>} data-id="element-1387" data-mp="3ryzgp4nwlpfi" />

      {/* Filtros */}
      <div className="mb-5 rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1397" data-mp="s4ia0hgoqy8w">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1398" data-mp="n5ntjm66xlry">
          {/* Buscador: la lupa va absolutamente posicionada y el campo
              reserva su espacio con padding, para que nunca se solapen. */}
          <div className="relative" data-id="element-1399" data-mp="x3brekz3f9ec">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1400" data-mp="x4d2uqk0txwl" />
            <input id="buscar" type="search" aria-label="Buscar leads" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1401" data-mp="2yvhsxi6b2qet" />
          </div>
          <Select id="f-estado" placeholder="Todos los estados" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={stageId} onChange={e => setStageId(e.target.value)} data-id="element-1402" data-mp="2jyqepqdqhvkk" />
          <Select id="f-form" placeholder="Todos los formularios" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1403" data-mp="2a0anzoimcobl" />
          <Select id="f-landing" placeholder="Todas las landings" options={landings.map(l => ({
          value: l.id,
          label: l.name
        }))} value={landingId} onChange={e => setLandingId(e.target.value)} data-id="element-1404" data-mp="3qgna5ou99ahp" />
          <Select id="f-campana" placeholder="Todas las campañas" options={campaigns.map(c => ({
          value: c,
          label: c
        }))} value={campaign} onChange={e => setCampaign(e.target.value)} data-id="element-1405" data-mp="3t1kxm8z6z886" />
          <Select id="f-comercial" placeholder="Todos los comerciales" options={members.map(m => ({
          value: m.userId,
          label: fullName(m.firstName, m.lastName)
        }))} value={owner} onChange={e => setOwner(e.target.value)} data-id="element-1406" data-mp="3jzlhdiw0486a" />
          <Input id="f-desde" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} data-id="element-1407" data-mp="glr145245dhy" />
          <Input id="f-hasta" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} data-id="element-1408" data-mp="bmxpyjaatyky" />
        </div>
      </div>

      {/* Acciones en lote */}
      {selected.length > 0 && hasPermission('leads.edit') && <div className="mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center" data-id="element-1409" data-mp="z9w3p971asf6">
          <p className="text-sm font-medium text-foreground" data-id="element-1410" data-mp="uinrk5ebksyb">
            {selected.length} leads seleccionados
          </p>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:justify-end" data-id="element-1411" data-mp="11wev29k6dlg6">
            <Select id="bulk" placeholder="Cambiar estado a…" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={bulkStage} onChange={e => setBulkStage(e.target.value)} data-id="element-1412" data-mp="16u6z21y3gy0q" />
            <Button onClick={applyBulk} loading={applying} disabled={!bulkStage} data-id="element-1413" data-mp="1gt5zix8phncn">
              Aplicar
            </Button>
            <Button variant="ghost" onClick={() => setSelected([])} data-id="element-1414" data-mp="1psja5t8kghyy">
              Cancelar
            </Button>
          </div>
        </div>}

      {/* Tabla escritorio */}
      <div className="hidden lg:block" data-id="element-1415" data-mp="1uk18san4a6gr">
        <Table data={paged} rowKey={lead => lead.id} selectable={hasPermission('leads.edit')} selectedKeys={selected} onSelectionChange={setSelected} emptyMessage="Ningún lead cumple estos filtros." columns={[{
        key: 'name',
        header: 'Nombre',
        sortable: true,
        render: lead => <button type="button" onClick={() => setDrawerLead(lead.id)} className="text-left font-medium text-foreground hover:text-primary" data-id="element-1417" data-mp="367ng4mbti0ec">
                  {fullName(lead.firstName, lead.lastName)}
                </button>
      }, {
        key: 'company',
        header: 'Empresa',
        render: lead => lead.company
      }, {
        key: 'phone',
        header: 'Teléfono',
        render: lead => lead.phone
      }, {
        key: 'email',
        header: 'Email',
        render: lead => lead.email
      }, {
        key: 'instagram',
        header: 'Instagram',
        render: lead => lead.instagram || '—'
      }, {
        key: 'city',
        header: 'Ciudad',
        render: lead => lead.city || '—'
      }, {
        key: 'service',
        header: 'Servicio solicitado',
        render: lead => lead.service || '—'
      }, {
        key: 'adBudget',
        header: 'Presup. publicidad',
        render: lead => lead.adBudget || '—'
      }, {
        key: 'budget',
        header: 'Presup. disponible',
        render: lead => lead.budget || '—'
      }, {
        key: 'source',
        header: 'Fuente',
        render: lead => <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1418" data-mp="j4konafk5g73" />
      }, {
        key: 'stage',
        header: 'Estado',
        render: lead => <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1419" data-mp="a233z0fjwvm7" />
      }, {
        key: 'owner',
        header: 'Comercial',
        render: lead => memberName(lead.assignedTo)
      }, {
        key: 'campaign',
        header: 'Campaña',
        render: lead => lead.origin.campaign || '—'
      }, {
        key: 'landing',
        header: 'Landing',
        render: lead => landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
      }, {
        key: 'form',
        header: 'Formulario',
        render: lead => forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
      }, {
        key: 'createdAt',
        header: 'Fecha',
        sortable: true,
        render: lead => formatDate(lead.createdAt)
      }, {
        key: 'lastContact',
        header: 'Último contacto',
        render: lead => lead.lastContactAt ? formatDate(lead.lastContactAt) : 'Sin contactar'
      }, {
        key: 'note',
        header: 'Última nota',
        render: lead => <NotePreview note={lastNote(lead.id)} data-id="element-1420" data-mp="1cemdzd6ngl9o" />
      }, {
        key: 'actions',
        header: '',
        render: lead => <FloatingMenu align="right" label="Acciones del contacto" trigger={<span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface" data-id="element-1422" data-mp="17m2yubsbdi2r">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1423" data-mp="xqhsy2k4jxnq" />
                    </span>} items={rowActions(lead)} data-id="element-1421" data-mp="17ne3edtl2tux" />
      }].filter(column => column.key === 'actions' || visibleColumns.includes(column.key))} data-id="element-1416" data-mp="1zi2qar4w9xq4" />
      </div>

      {/* Tarjetas móvil */}
      <ul className="grid gap-3 lg:hidden" data-id="element-1424" data-mp="orea8zovdk9r">
        {paged.map(lead => {
        const note = lastNote(lead.id);
        const expanded = expandedCard === lead.id;
        return <li key={lead.id} className="rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1425" data-mp="359x38tg4rst8">
              <div className="flex items-start justify-between gap-3" data-id="element-1426" data-mp="39dv0szpt4vk6">
                <div className="min-w-0" data-id="element-1427" data-mp="3eb3jr2is53mh">
                  <p className="truncate font-semibold text-foreground" data-id="element-1428" data-mp="3e9sk9ewdai16">
                    {fullName(lead.firstName, lead.lastName)}
                  </p>
                  <p className="truncate text-sm text-muted-foreground" data-id="element-1429" data-mp="2loaimj1ha2by">
                    {lead.company}
                  </p>
                </div>
                <FloatingMenu align="right" label="Acciones del contacto" trigger={<span className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground" data-id="element-1431" data-mp="2vevgu5d8p4uc">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1432" data-mp="1xw2dg0dv5fnu" />
                    </span>} items={rowActions(lead)} data-id="element-1430" data-mp="2vdaie4nt11r7" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1433" data-mp="waqdg3914j7n">
                <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1434" data-mp="rauam7c2ixto" />
                <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1435" data-mp="1190bqmq1vzxh" />
              </div>

              {note && <div className={`mt-3 rounded-lg border-l-4 px-3 py-2 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1436" data-mp="1f89ofcf1sbwp">
                  <p className="line-clamp-2 text-xs leading-snug text-foreground" data-id="element-1437" data-mp="1a8nfhf695htp">
                    {note.body}
                  </p>
                </div>}

              {expanded && <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs text-muted-foreground" data-id="element-1438" data-mp="1jyq6xaxq6npx">
                  <div className="truncate" data-id="element-1439" data-mp="1y02hf6u49x4f">{lead.email}</div>
                  <div className="truncate" data-id="element-1440" data-mp="1t07t9b1q7ln2">{lead.phone}</div>
                  <div className="truncate" data-id="element-1441" data-mp="1t1r950jgzuob">{lead.city || '—'}</div>
                  <div className="truncate" data-id="element-1442" data-mp="2cmrhpaw70ebv">{lead.service || '—'}</div>
                  <div className="truncate" data-id="element-1443" data-mp="2hkkljxa0ww8z">{lead.budget || '—'}</div>
                  <div className="truncate" data-id="element-1444" data-mp="2hiqadaok0ii3">{formatDate(lead.createdAt)}</div>
                </dl>}

              <div className="mt-3 flex gap-2" data-id="element-1445" data-mp="23n4dyifzqwfn">
                <Button variant="ghost" size="sm" className="min-h-[44px] flex-1" onClick={() => setExpandedCard(expanded ? null : lead.id)} data-id="element-1446" data-mp="27lve6yfr6u6z">
                  {expanded ? 'Ver menos' : 'Ver más'}
                </Button>
                <Button variant="secondary" size="sm" className="min-h-[44px] flex-1" onClick={() => setDrawerLead(lead.id)} data-id="element-1447" data-mp="1ymqehosggut8">
                  Abrir ficha
                </Button>
              </div>
            </li>;
      })}
      </ul>

      {/* Paginación */}
      <nav className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" aria-label="Paginación de leads" data-id="element-1448" data-mp="1awmb9ij6ix2r">
        <p className="order-2 text-sm text-muted-foreground lg:order-1" data-id="element-1449" data-mp="1fuflqnrimd8u">
          <span className="hidden sm:inline" data-id="element-1450" data-mp="1e22f6ye0mijs">
            Mostrando {rangeFrom}–{rangeTo} de {filtered.length} contactos
          </span>
          <span className="sm:hidden" data-id="element-1451" data-mp="1j38bf66vwny2">
            {rangeFrom}–{rangeTo} de {filtered.length}
          </span>
        </p>

        <div className="order-3 flex flex-wrap items-center justify-center gap-2 lg:order-2" data-id="element-1452" data-mp="153yxkd65i5x9">
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} data-id="element-1453" data-mp="1a3338ttv9liu">
            Anterior
          </Button>
          {totalPages > 1 && <ul className="hidden items-center gap-1 sm:flex" data-id="element-1454" data-mp="21u94xep8jra8">
              {pageNumbers.map(number => <li key={number} data-id="element-1455" data-mp="1o0hzahemkrjs">
                  <button type="button" aria-label={`Ir a la página ${number}`} aria-current={number === currentPage ? 'page' : undefined} onClick={() => setPage(number)} className={`min-h-[44px] min-w-[44px] rounded-xl px-3 text-sm font-medium transition-colors ${number === currentPage ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-surface hover:text-foreground'}`} data-id="element-1456" data-mp="1szl5442fj1st">
                    {number}
                  </button>
                </li>)}
            </ul>}
          <span className="text-sm text-muted-foreground sm:hidden" data-id="element-1457" data-mp="34kr54mv2wgic">
            {currentPage} / {totalPages}
          </span>
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} data-id="element-1458" data-mp="2zle7r86kzr1i">
            Siguiente
          </Button>
        </div>

        {/* Contactos por página */}
        <div className="order-1 flex items-center gap-2 lg:order-3" data-id="element-1459" data-mp="27wb2bp2ykana">
          <label htmlFor="leads-per-page" className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-1460" data-mp="2lvav98xi7bp9">
            Contactos por página
          </label>
          <select id="leads-per-page" value={pageSize} onChange={e => {
          updatePreferences({
            leadsPerPage: Number(e.target.value)
          });
          setPage(1);
        }} className="h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1461" data-mp="141xsyno92rm">
            {LEADS_PER_PAGE_OPTIONS.map(option => <option key={option} value={option} data-id="element-1462" data-mp="d6bk34j2rj6d">
                {option}
              </option>)}
          </select>
        </div>
      </nav>

      <LeadDrawer leadId={drawerLead} onClose={() => setDrawerLead(null)} data-id="element-1463" data-mp="i43ec576zhdq" />

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1464" data-mp="3gnthei1817sj" />
      <SourceManager open={sourcesOpen} onClose={() => setSourcesOpen(false)} organizationId={organizationId} sources={sources} data-id="element-1465" data-mp="3lmwos94obq3o" />

      <ConfirmDialog open={Boolean(confirmTrash)} title="Mover a la papelera" description="El lead saldrá de Leads y del embudo, pero conservará sus notas, respuestas e historial. Podrás restaurarlo desde la Papelera." confirmLabel="Mover a la papelera" loading={trashing} onCancel={() => setConfirmTrash(null)} onConfirm={async () => {
      if (!confirmTrash) return;
      setTrashing(true);
      await deleteLead(confirmTrash.id, authorName);
      setTrashing(false);
      setConfirmTrash(null);
      if (drawerLead === confirmTrash.id) setDrawerLead(null);
    }} data-id="element-1466" data-mp="3qj50tdnkr36m" />

      {/* Panel flotante: se puede mover para no tapar la tabla y los cambios
          se reflejan al instante detrás. */}
      <FloatingPanel open={columnsOpen} title="Personalizar columnas" description="Los cambios se aplican en la tabla al instante." position={preferences.columnPanelPosition} onPositionChange={position => updatePreferences({
      columnPanelPosition: position
    })} onClose={() => setColumnsOpen(false)} footer={<div className="flex flex-wrap gap-2" data-id="element-1468" data-mp="22l49k1fjtfmi">
            <Button size="sm" variant="ghost" onClick={() => setVisibleColumns(COLUMN_OPTIONS.map(c => c.key))} data-id="element-1469" data-mp="1xpf6vp88filz">
              Mostrar todas
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setVisibleColumns([...DEFAULT_COLUMNS])} data-id="element-1470" data-mp="1onxr67yqlixt">
              Restaurar
            </Button>
            <Button size="sm" onClick={() => setColumnsOpen(false)} data-id="element-1471" data-mp="2devbxf0t0gtj">
              Listo
            </Button>
          </div>} data-id="element-1467" data-mp="2ggy8psqisqmf">
        <ul className="grid gap-2" data-id="element-1472" data-mp="2ifixvma6e7gw">
          {COLUMN_OPTIONS.map(column => <li key={column.key} data-id="element-1473" data-mp="24l8k1zzpvxpm">
              <Checkbox id={`col-${column.key}`} label={column.label} checked={visibleColumns.includes(column.key)} onChange={e => setVisibleColumns(prev => e.target.checked ? [...prev, column.key] : prev.filter(k => k !== column.key))} data-id="element-1474" data-mp="24k76nov8az4q" />
            </li>)}
        </ul>
      </FloatingPanel>
    </>;
}

/**
 * Vista previa de la nota más reciente: dos líneas como máximo y la nota
 * completa al pasar el cursor o al pulsar en móvil.
 */
function NotePreview({
  note
}: {
  note?: LeadNote;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);

  /**
   * El contenido completo se pinta con un portal en `document.body` y posición
   * fija, así nunca queda recortado dentro de la fila ni por el scroll de la
   * tabla. Si no cabe debajo, se coloca encima.
   */
  function show() {
    const box = anchor.current?.getBoundingClientRect();
    if (!box) return;
    const width = 288;
    const below = window.innerHeight - box.bottom > 200;
    setPos({
      top: below ? box.bottom + 8 : Math.max(8, box.top - 208),
      left: Math.min(Math.max(8, box.left), window.innerWidth - width - 8)
    });
    setOpen(true);
  }
  if (!note) return <span className="text-muted-foreground" data-id="element-1475" data-mp="1n3t7llto4mu4">Sin notas</span>;
  const style = noteColor(note.color);
  const preview = note.body.length > 90 ? `${note.body.slice(0, 90).trim()}…` : note.body;
  return <span ref={anchor} className="block w-56" onMouseEnter={show} onMouseLeave={() => setOpen(false)} data-id="element-1476" data-mp="1w6as9vtod7f0">
      <button type="button" onClick={e => {
      e.stopPropagation();
      if (open) setOpen(false);else show();
    }} className={`flex w-full items-start gap-2 rounded-lg border-l-4 px-2.5 py-2 text-left ${style.bg} ${style.border}`} data-id="element-1477" data-mp="6ql6dujpvb1d">
        <span className={`mt-1 h-2 w-2 flex-none rounded-full ${style.dot}`} aria-hidden="true" data-id="element-1478" data-mp="26sb0s84h519g" />
        <span className="min-w-0" data-id="element-1479" data-mp="2brf96wjkquyq">
          {note.title && <span className="block truncate text-xs font-semibold text-foreground" data-id="element-1480" data-mp="2bqmbsm6i9ht9">
              {note.title}
            </span>}
          <span className="line-clamp-2 text-xs leading-snug text-foreground/80" data-id="element-1481" data-mp="2fqwf1r2wxu4f">
            {preview}
          </span>
        </span>
      </button>
      {open && pos && createPortal(<span style={{
      top: pos.top,
      left: pos.left
    }} className={`fixed z-[70] block max-h-52 w-72 overflow-auto rounded-xl border border-border border-l-4 p-3 text-xs leading-relaxed text-foreground shadow-xl ${style.bg} ${style.border}`} data-id="element-1482" data-mp="2pplz9qia7qnu">
            <span className="mb-1 block text-[11px] font-medium text-muted-foreground" data-id="element-1483" data-mp="33j3fp2fo8n9n">
              {note.authorName} · {formatDateTime(note.createdAt)}
            </span>
            {note.body}
          </span>, document.body)}
    </span>;
}
function useWorkspaceMembers() {
  const {
    members
  } = useAuth();
  return members;
}